<template>
  <div v-if="isAdmin || isStaff" class="countries-container">
    <div class="header">
      <h1>{{ 'activities.countries' | translate }}</h1>
      <div class="header-controls">
        <UiSearchInput 
          v-model="searchQuery"
          :placeholder="'activities.search' | translate"
          @search-input="onSearchInput"
          class="search-component"
        />
        <v-btn :disabled="isStaff" class="add-country-btn ml-4" medium @click="showAddModal = true">
          <v-icon position-left font-size="1" icon="plus"></v-icon>
          <span>{{ 'activities.add_country' | translate }}</span>
        </v-btn>
      </div>
    </div>

    <!-- Skeleton Loader -->
    <div v-if="loading" class="countries-grid">
      <div 
        v-for="n in 6" 
        :key="`skeleton-${n}`" 
        class="country-card skeleton-card"
      >
        <div class="country-header">
          <div class="country-flag-name">
            <div class="skeleton-flag skeleton-shimmer"></div>
            <div class="country-info">
              <div class="skeleton-title skeleton-shimmer"></div>
              <div class="skeleton-time skeleton-shimmer"></div>
              <div class="skeleton-count skeleton-shimmer"></div>
            </div>
          </div>
          <div class="skeleton-menu skeleton-shimmer"></div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="error-container">
      <p>{{ 'activities.error' | translate }} {{ 'activities.loading' | translate }}: {{ error }}</p>
      <v-btn @click="loadCountries">{{ 'activities.try_again' | translate }}</v-btn>
    </div>

    <div v-else class="countries-grid">
      <div v-for="country in countries" :key="country.id" class="country-card" @click="onCountryClick(country)">
        <div class="country-header">
          <div class="country-flag-name">
            <div class="flag"
              :style="{ backgroundImage: country.logo ? `url(${country.logo})` : 'none', backgroundColor: country.logo ? 'transparent' : getRandomFlagColor() }">
            </div>
            <div class="country-info">
              <h3>{{ country.name }}</h3>
              <div class="country-time">
                {{ currentDateTime(country.timezone_name) }}
              </div>
              <p class="company-count">{{ country.client_count || 0 }} {{ getCompanyText(country.client_count || 0) }}
              </p>
            </div>
          </div>
          <div v-if="!isStaff" class="country-menu" @click.stop>
            <settings-country-dropdown :id="country.id" @edit="openEditModal(country)" @delete="deleteCountry" v-if="country.id"/>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && !error && countries.length === 0" class="empty-state">
      <p v-if="searchQuery">{{ 'activities.country_search' | translate }} "{{ searchQuery }}" {{ 'activities.not_found' | translate }}</p>
      <p v-else>{{ 'activities.not_found_countries' | translate }}</p>
    </div>

    <v-dialog max-width="400" v-model="showAddModal" @close="closeModal">
      <AddCountryModal @submit="handleCountrySubmit" @close="closeModal" />
    </v-dialog>

    <v-dialog max-width="400" v-model="showEditModalFlag" @close="closeEditModal">
      <EditCountryModal :country="editingCountry" @submit="handleCountryUpdate" @delete="deleteCountry"
        @close="closeEditModal" />
    </v-dialog>
  </div>
  <div v-else>Access denied!</div>
</template>

<script>
import AddCountryModal from './modals/AddCountryModal'
import EditCountryModal from './modals/EditCountryModal'
import SettingsCountryDropdown from './dropdowns/SettingsCountryDropdown'
import DeleteBtn from '@/components/common/delete-btn/DeleteBtn'
import UiSearchInput from '@/components/ui/ui-search-input/UiSearchInput'
import { mapGetters, mapActions } from 'vuex'
import { LOCALE_MAP } from '@/util'

export default {
  name: 'CountriesComponent',

  components: {
    DeleteBtn,
    SettingsCountryDropdown,
    AddCountryModal,
    EditCountryModal,
    UiSearchInput
  },

  data () {
    return {
      searchQuery: '',
      showAddModal: false,
      showEditModalFlag: false,
      editingCountry: null,
      loading: false,
      error: null,
      countries: [],
      searchTimeout: null
    }
  },

  computed: {
    ...mapGetters('countriesGroups', [
      'countriesGroups'
    ])
  },

  methods: {
    ...mapActions('countriesGroups', [
      'loadCountriesGroups',
      'createCountriesGroup',
      'updateCountriesGroup',
      'deleteCountriesGroup',
      'selectCountriesGroup'
    ]),
    timezoneDateTime (timezoneName, langCode = 'uk', localeMap = {}) {
      if (!timezoneName) return ''

      try {
        const now = new Date()
        const locale = localeMap[langCode] || 'uk-UA'
        const options = {
          timeZone: timezoneName,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: langCode === 'us'
        }

        return new Intl.DateTimeFormat(locale, options).format(now)
      } catch (error) {
        console.error('Помилка при форматуванні часу:', error)
        return timezoneName
      }
    },
    currentDateTime (timezone) {
      return this.timezoneDateTime(
        timezone,
        this.$store.getters['account/lang'],
        LOCALE_MAP
      )
    },
    onSearchInput () {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.loadCountries()
      }, 300)
    },
    async loadCountries () {
      try {
        this.loading = true
        this.error = null
        const params = {}
        if (this.searchQuery) {
          params.name = this.searchQuery.trim()
        } else {
          params.name = null
        }
        await this.loadCountriesGroups(params)
        this.countries = this.countriesGroups

        this.countries.push({
          id: null,
          name: this.$t('activities.without_group'),
          timezone_name: null,
          logo: null,
          client_count: '-',
          created_at: null
        })
      } catch (error) {
        this.error = error.message || this.$t('activities.country_loading_err')
      } finally {
        this.loading = false
      }
    },
    async loadCountriesGroupsWithSearch (params = {}) {
      try {
        await this.loadCountriesGroups(params)

        return this.countriesGroups
      } catch (error) {
        throw error
      }
    },

    onCountryClick (country) {
      if (this.$route.path === '/app/activities') {
        this.$router.push(`/app/activities/${country.id}`)
      }
    },

    async handleCountrySubmit (formData) {
      try {
        const { errors } = await this.createCountriesGroup(formData)

        if (!errors) {
          this.showAddModal = false

          await this.loadCountries()

          this.$toasted.success(this.$t('activities.country_success'))
        } else {
          console.error('Помилки створення:', errors)
          this.$toasted.error(this.$t('activities.country_err'))
        }
      } catch (error) {
        console.error('Помилка створення країни:', error)
        this.$toasted.error(this.$t('activities.country_err'))
      }
    },

    async openEditModal (country) {
      try {
        await this.selectCountriesGroup(country.id)
        this.editingCountry = { ...country }
        this.showEditModalFlag = true
      } catch (error) {
        console.error('Помилка завантаження даних країни:', error)
        this.$toasted.error(this.$t('activities.country_loading_err'))
      }
    },

    async handleCountryUpdate (countryData) {
      try {
        const errors = await this.updateCountriesGroup({
          id: countryData.id,
          payload: countryData.payload
        })

        if (!errors) {
          this.closeEditModal()

          await this.loadCountries()
          this.$toasted.success(this.$t('activities.country_updated'))
        } else {
          console.error('Помилки оновлення:', errors)
          this.$toasted.error(this.$t('activities.error'))
        }
      } catch (error) {
        console.error('Помилка оновлення країни:', error)
        this.$toasted.error(this.$t('activities.error'))
      }
    },

    async deleteCountry (countryId) {
      try {
        await this.selectCountriesGroup(countryId)

        await this.deleteCountriesGroup()

        await this.loadCountries()

        this.$toasted.success(this.$t('activities.country_removed'))
      } catch (error) {
        console.error(this.$t('activities.error'), error)
        this.$toasted.error(this.$t('activities.error'))
      }
    },

    getRandomFlagColor () {
      const colors = ['#4285f4', '#ea4335', '#34a853', '#fbbc05', '#333', '#9c27b0']
      return colors[Math.floor(Math.random() * colors.length)]
    },

    closeEditModal () {
      this.showEditModalFlag = false
      this.editingCountry = null
    },

    closeModal () {
      this.showAddModal = false
    },

    getCompanyText (count) {
      if (count === 1) return this.$t('activities.company')
      if (count >= 2 && count <= 4) return this.$t('activities.companies')
      return this.$t('activities.companies')
    }
  },
  async mounted () {
    await this.loadCountries()
  },
  beforeDestroy () {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    document.removeEventListener('click', this.handleOutsideClick)
  }
}
</script>

<style lang="scss" scoped>
.countries-container {
  margin: 0 auto;
  width: 100%;
  padding: 2rem 0;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #333;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
  margin-left: 4rem;
  justify-content: space-between;
}

.search-component {
  width: 100%;
  max-width: 31.5rem;
  flex: 1;
}

.loading-container,
.error-container,
.empty-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.error-container {
  color: #d32f2f;
}

.countries-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.country-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 1rem;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
}

.country-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.country-flag-name {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.flag {
  width: 6rem;
  height: 6rem;
  border-radius: 10px;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.country-info h3 {
  margin: 0 0 4px 0;
  font-size: 17px;
  font-weight: 900;
  font-family: "Museo Sans", sans-serif;
  color: #000;
}

.company-count {
  margin: 0;
  font-size: 15px;
  color: #000;
  font-weight: 500;
  font-family: "Museo Sans", sans-serif;
}

.country-menu {
  position: relative;
}

.country-time {
  font-size: 12px;
  color: #979797;
  margin-top: 0.6rem;
  font-family: "Museo Sans", sans-serif;
}

/* Skeleton Loader Styles */
.skeleton-card {
  pointer-events: none;
}

.skeleton-flag {
  width: 6rem;
  height: 6rem;
  border-radius: 10px;
  background-color: #f5f5f5;
}

.skeleton-title {
  width: 120px;
  height: 20px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-time {
  width: 80px;
  height: 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin: 0.6rem 0 8px 0;
}

.skeleton-count {
  width: 100px;
  height: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-menu {
  width: 24px;
  height: 24px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-shimmer {
  position: relative;
  overflow: hidden;
}

.skeleton-shimmer::after {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s infinite;
  content: '';
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 768px) {
  .header {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }

  .header-controls {
    justify-content: space-between;
    margin-left: 0;
  }

  .countries-grid {
    grid-template-columns: 1fr;
  }
}
</style>